import { nextTick } from '@vue/composition-api';
import { defineStore } from 'pinia';
import { EditDiscussionState, MessageState } from '@front-app/components/discussion/Model';
import {
	CreateDiscussionPayloadV2,
	DiscussionContentItem,
	StoredDiscussionContentItem,
} from '@front-app/services/GroupsApi/DiscussionModel';
import {
	createDiscussionV2,
	getDiscussionByIdV2,
	GroupDiscussionFollowAction,
	replyToDiscussionV2,
	toggleGroupDiscussionFollowStatus,
} from '@front-app/services/GroupsApi/GroupsDiscussionApi';
import { getReplyDiscussionPayload } from '@front-app/components/discussion/util';
import { CurrentUserStore } from '../singletons/User';
import { useDiscussionMessageStore } from './discussionMessages';

export interface DiscussionStoreState {
	discussions: StoredDiscussionContentItem[];
}

export const useDiscussionStore = defineStore('discussionStore', {
	state: (): DiscussionStoreState => ({
		discussions: [],
	}),
	getters: {
		getById(state) {
			return (discussionId: string) => state.discussions.find(({ id }) => id === discussionId);
		},
		isFollowing() {
			return (discussionId: string) => {
				const discussion = this.getById(discussionId);
				if (!discussion) {
					return false;
				}
				const currentUserId = CurrentUserStore.currentUser.value.id;
				const currentUserAsFollower = discussion.metadata.discussion
					?.followers
					.find(({ id }) => id === currentUserId);
				return Boolean(currentUserAsFollower);
			};
		},
	},
	actions: {
		update(newDiscussions: StoredDiscussionContentItem[]) {
			this.$patch((state) => {
				const discussionIds = newDiscussions.map(({ id }) => id);
				const oldDiscussions = state.discussions.filter(({ id }) => !discussionIds.includes(id));
				state.discussions = oldDiscussions.concat(newDiscussions);
			});
		},
		setWithMessages(newDiscussions: DiscussionContentItem[]) {
			const messages = newDiscussions.flatMap(({ metadata }) => metadata.discussion.messages);
			useDiscussionMessageStore().set(messages);

			const discussionIds = newDiscussions.map(({ id }) => id);
			const discussionsToStore = newDiscussions.map(toStoredDiscussion);
			this.$patch((state) => {
				const oldDiscussions = state.discussions.filter(({ id }) => !discussionIds.includes(id));
				state.discussions = oldDiscussions.concat(discussionsToStore);
			});
		},
		async fetch(groupId: string, discussionId: string) {
			const newDiscussion = await getDiscussionByIdV2(groupId, discussionId);
			// The API sets the discussionId to the ID of the latest post, which breaks things
			newDiscussion.id = discussionId;
			this.setWithMessages([newDiscussion]);
			await nextTick();
		},
		async create(groupId: string, discussion: CreateDiscussionPayloadV2) {
			const newDiscussion = await createDiscussionV2(groupId, discussion);
			this.setWithMessages([newDiscussion]);
			return newDiscussion.id;
		},
		async toggleFollow(discussionId: string): Promise<void> {
			const discussion = this.getById(discussionId);
			if (!discussion) {
				return Promise.reject();
			}
			const action = this.isFollowing(discussionId)
				? GroupDiscussionFollowAction.UNFOLLOW
				: GroupDiscussionFollowAction.FOLLOW;
			await toggleGroupDiscussionFollowStatus(discussion.id, action);
			this.update([toggleLocalFollowState(discussion, this.isFollowing(discussionId))]);
			return Promise.resolve();
		},
		async edit(discussionId: string, payload: Partial<EditDiscussionState>) {
			const content = this.getById(discussionId);
			if (!content) {
				return Promise.reject();
			}
			const idx = content.metadata.discussion.messages.length || 1;
			const messageId = content.metadata.discussion.messages[idx - 1];
			if (!messageId) {
				return Promise.reject();
			}
			const message = await useDiscussionMessageStore().edit(messageId, payload);
			content.name = message.subject;
			content.categories = message.categories;
			return Promise.resolve();
		},
		async reply(groupId: string, discussionId: string, messageState: MessageState) {
			const theContent = this.getById(discussionId);
			if (!theContent) {
				// eslint-disable-next-line no-console
				console.error('Could not reply to non-existant discussion');
				return Promise.reject();
			}
			const parentId = theContent.metadata.discussion.messages[theContent.metadata.discussion.messages.length - 1];
			if (parentId === undefined) {
				// eslint-disable-next-line no-console
				console.error('Could not reply to non-existant discussion');
				return Promise.reject();
			}
			const parent = useDiscussionMessageStore().getById(parentId);
			if (parent === undefined) {
				// eslint-disable-next-line no-console
				console.error('Could not reply to non-existant discussion');
				return Promise.reject();
			}
			const payload = getReplyDiscussionPayload(
				messageState,
				theContent,
				this.isFollowing(discussionId),
			);
			const result = await replyToDiscussionV2(groupId, parentId, parent.version, payload);
			// The API sets the discussionId to the ID of the latest post, which breaks things
			result.id = discussionId;
			this.setWithMessages([result]);
			return Promise.resolve();
		},
	},
});

function toStoredDiscussion(discussion: DiscussionContentItem): StoredDiscussionContentItem {
	return {
		...discussion,
		metadata: {
			...discussion.metadata,
			discussion: {
				...discussion.metadata.discussion,
				messages: discussion.metadata.discussion.messages.map(({ id }) => id),
			},
		},
	};
}

export function toggleLocalFollowState(content: StoredDiscussionContentItem, isFollowing: boolean): StoredDiscussionContentItem {
	if (isFollowing) {
		return setUnfollowed(content);
	}
	return setFollowed(content);
}

function setFollowed(content: StoredDiscussionContentItem): StoredDiscussionContentItem {
	// Add current user to the followers
	const { discussion } = content.metadata;
	discussion.followers = [...discussion.followers, CurrentUserStore.currentUser.value];
	return content;
}

function setUnfollowed(content: StoredDiscussionContentItem): StoredDiscussionContentItem {
	// Remove current user from the followers
	const { discussion } = content.metadata;
	const currentUserId = CurrentUserStore.currentUser.value.id;
	discussion.followers = discussion.followers.filter(({ id }) => id !== currentUserId);
	return content;
}
